import { init, Models, RematchDispatch, RematchRootState } from '@rematch/core';
import persistPlugin from '@rematch/persist';
import storage from 'redux-persist/lib/storage';
import immerPlugin from '@rematch/immer';
import { enableMapSet } from 'immer';

// import_model
import { auth } from './auth.store';
import { systemUser } from './system-user.store';
import { device } from './devices.store';
import { alert } from './alert.store';
import { report } from './reports.store';
import { user } from './user.store';
import { customer } from './customer.store';
import { customersAdmin } from './customers-admin.store';
import { metrics } from './metrics.store';
import { eventMap } from './event-map.store';
import { geofences } from './geofence.store';
import { appointment } from './appointments.store';
import { insight } from './insights.store';
import { schedules } from './schedules.store';
import { groups } from './groups.store';
import { tags } from './tags.store';
import { teams } from './teams.store';
import { mobileAppUser } from './mobile-app-user.store';
import { userActivity } from './user-activity.store';
import { messageCenter } from './message-center.store';
import { groupsTeams } from './groups-teams.store';
import { myDashboard } from './my-dashboard.store';
import { bannersAdmin } from './banners-admin.store';
import { auditLogExplorer } from './auditLogExplorer.store';

enableMapSet();

const persistConfig = {
    key: 'pom-admin-core-persistor',
    storage,
    whitelist: ['auth']  // Only persist auth; customer data is refetched on load via whoIam()
};

// Stores to preserve during masquerade reset.
// Keep only auth/token; all tenant-scoped state must be rehydrated after whoIam().
const PRESERVE_ON_RESET = ['auth'];

export const store = init<RootModel>({
    models: {
        // use_model
        auth,
        systemUser,
        device,
        alert,
        report,
        user,
        customer,
        customersAdmin,
        metrics,
        eventMap,
        geofence: geofences,
        appointment,
        schedule: schedules,
        insight,
        groups: groups,
        tags: tags,
        teams: teams,
        mobileAppUser: mobileAppUser,
        userActivity,
        messageCenter,
        groupsTeams,
        myDashboard,
        bannersAdmin,
        auditLogExplorer,
    },
    plugins: [persistPlugin(persistConfig), immerPlugin()],
    redux: {
        rootReducers: {
            // Global reset: wipes all subscription-scoped stores while preserving auth context.
            // Used during masquerade to ensure clean state, like a fresh login.
            // When a store key is missing from the returned state, Rematch re-initializes
            // it with the model's initial state automatically.
            RESET_ALL_STORES: (state: any) => {
                const preserved: any = {};
                PRESERVE_ON_RESET.forEach(key => {
                    if (state[key]) preserved[key] = state[key];
                });
                return preserved;
            },
        },
    },
});

/**
 * Reset all subscription-scoped stores to initial state.
 * Preserves only auth store.
 * Call this during masquerade (before whoIam) and un-masquerade
 * to prevent cross-subscription data leakage.
 */
export const resetAllStores = () => store.dispatch({ type: 'RESET_ALL_STORES' });

export interface RootModel extends Models<RootModel> {
    // type_model
    auth: typeof auth;
    systemUser: typeof systemUser;
    device: typeof device;
    alert: typeof alert;
    report: typeof report;
    user: typeof user;
    customer: typeof customer;
    customersAdmin: typeof customersAdmin;
    metrics: typeof metrics;
    eventMap: typeof eventMap;
    geofence: typeof geofences;
    appointment: typeof appointment,
    schedule: typeof schedules,
    groups: typeof groups,
    tags: typeof tags,
    insight: typeof insight,
    teams: typeof teams,
    mobileAppUser: typeof mobileAppUser,
    userActivity: typeof userActivity,
    messageCenter: typeof messageCenter,
    groupsTeams: typeof groupsTeams,
    myDashboard: typeof myDashboard,
    bannersAdmin: typeof bannersAdmin,
    auditLogExplorer: typeof auditLogExplorer,
}

export type Store = typeof store;
export type Dispatch = RematchDispatch<RootModel>;
export type RootState = RematchRootState<RootModel>;
